import Debug from 'debug'
import { sqliteBroker } from '../services/SqliteBroker';
const log = Debug('pdq:sqlite:client');

const useShared = false
let port: { postMessage: (arg0: any) => void; }

export class PqdSqlClient {
  #opfsPath: string;
  #worker: Worker|SharedWorker;
  #messageId: number = 0;
  #subscribers: Map<number, { sql?: string; resolve: (value: any) => void; reject: (reason?: any) => void; url?: string; alias?: string; path?: string }> = new Map();
  #ready: Promise<void>;

  get ready (){
    return this.#ready
  }

  constructor(opfsPath: string) {
    log(`ctor: ${opfsPath}`)
    this.#opfsPath = opfsPath;
    const workerUrl = new URL(`./pdq-sqlite-worker.js`, location.href.substring(0, location.href.lastIndexOf('/')+1))
    if (useShared) {
      this.#worker = new SharedWorker(workerUrl, { type: 'module' });
      this.#worker.port.onmessage = this.#onmessage;
      port = this.#worker.port
    } else {
      this.#worker = new Worker(workerUrl, { type: 'module' });
      this.#worker.onmessage = this.#onmessage;
      port = this.#worker
    }
  }

  forceTerminateAndReloadPage(){
    if(this.#worker.terminate)
      this.#worker.terminate()
    location.reload()
  }

  query<T = any>(sql: string, params?: any[]): Promise<T[]> {
    const msgId = this.#messageId++;
    let start: number|undefined
    if (log.enabled) {
      start = performance.now();
      log(`query:[${msgId}] "${sql}"`, params)
    }
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, {
        sql,
        resolve: (resp) => {
          if (log.enabled) {
            log(`query:[${msgId}] success in ${performance.now() - start!}ms`, resp)
          }
          resolve(resp)
        },
        reject: (e) => {
          if (log.enabled) {
            log(`query:[${msgId}] failed in ${performance.now() - start!}ms`, e)
          }
          reject(e)
        }
      });
      await this.#ready;
      port.postMessage({ id: msgId, action: 'query', payload: { path:this.#opfsPath, sql, params } });
    });
  }

  private async prepare(verb: string, sql: string) : Promise<number> {
    const msgId = this.#messageId++;
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, { resolve: (i: {id: number}) => {resolve(i.id);}, reject });
      await this.#ready;
      port.postMessage({ id: msgId, action: 'prepare-statement',
        payload: {path:this.#opfsPath, sql, verb} });
    });
  }

  executeStatement(statement: PdqSqlitePreparedStatement) {
    const msgId = this.#messageId++;
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, { resolve, reject });
      await this.#ready;
      log('executeStatement:', statement, statement.getPayload());
      port.postMessage({
        id: msgId,
        action: 'execute-prepared-statement',
        payload: statement.getPayload() });
    });
  }

  finalizeStatement(statement: PdqSqlitePreparedStatement) {
    const msgId = this.#messageId++;
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, { resolve, reject });
      await this.#ready;
      port.postMessage({
        id: msgId,
        action: 'finalize-prepared-statement',
        payload: {
          id: statement.id,
          path:this.#opfsPath,
        }
      });
    });
  }

  async download(url: string, path: string, overwrite=true): Promise<any> {
    const msgId = this.#messageId++;
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, { url, resolve, reject });
      await this.#ready;
      port.postMessage({ id: msgId, action: 'download', payload: { url, path, overwrite } });
    });
  }

  async import(path: string, blob: Blob, overwrite=true): Promise<any> {
    const msgId = this.#messageId++;
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, { path, resolve, reject });
      await this.#ready;
      port.postMessage({ id: msgId, action: 'import', payload: { path, blob, overwrite } });
    });
  }

  async open(path: string): Promise<any> {
    const msgId = this.#messageId++;
    return new Promise(async (resolve, reject) => {
      this.#subscribers.set(msgId, { path, resolve, reject });
      await this.#ready;
      port.postMessage({id:msgId, action: 'open', payload: { path: this.#opfsPath } });
    });
  }

  #onmessage = (evt: MessageEvent): void => {
    log('onmessage', evt.data);
    const { id, action, error, result } = evt.data;
    switch (action) {
      case 'ready':
        port.postMessage({ id:-1, action: 'open', payload: { path: this.#opfsPath } });
        break;
      case 'open-result':
        log('database open');
        this.#subscribers.get(id)?.resolve(result);
        this.#subscribers.delete(id);
        break;
      case 'query-result':
      case 'download-result':
      case 'prepare-result':
        this.#subscribers.get(id)?.resolve(result);
        this.#subscribers.delete(id);
        break;
      case 'error':
        const sub = this.#subscribers.get(id);
        if (sub) {
          sub.reject({ error: error, sql: sub.sql });
          this.#subscribers.delete(id);
        } else {
          console.error(error);
        }
        break;
    }
  }

  async prepareRun(sql: string) {
    const id = await this.prepare('run', sql);
    return new PdfSqlitePreparedRunStatement(this, id, 'run', this.#opfsPath);
  }
  async prepareRunBulk(sql: string) {
    const id = await this.prepare('run-bulk', sql);
    return new PdfSqlitePreparedRunBulkStatement(this, id, 'run-bulk', this.#opfsPath);
  }
  async prepareGet(sql: string) {
    const id = await this.prepare('get', sql);
    return new PdfSqlitePreparedGetStatement(this, id, 'get', this.#opfsPath);
  }
  async prepareAll(sql: string) {
    const id = await this.prepare('all', sql);
    return new PdfSqlitePreparedAllStatement(this, id, 'all', this.#opfsPath);
  }
  async prepareBind(sql: string) {
    const id = await this.prepare('all', sql);
    return new PdfSqlitePreparedBindStatement(this, id, 'all', this.#opfsPath);
  }
}


abstract class PdqSqlitePreparedStatement {

  get id(): number {
    return this._id
  }
  get verb(): string {
    return this._verb
  }
  get path(): string {
    return this._path
  }
  constructor(
    protected _client: PqdSqlClient,
    protected _id: number,
    protected _verb: string,
    protected _path: string,
  ) {
    log(`PREPARED STATEMENT - ${this._id}`)
  }

  async dispose() {
    await this._client.finalizeStatement(this)
  }

  abstract getPayload(params?: any): {id: number, verb: string, params: any, path: string}
}
export class PdfSqlitePreparedRunStatement extends PdqSqlitePreparedStatement {
  private _params: any = undefined

  run(params: any = undefined): Promise<any> {
    this._params = params
    return this._client.executeStatement(this)
  }
  getPayload(): { id: number; verb: string; params: any } {
    return { id: this._id, params: this._params, verb: this._verb, path: this._path };
  }
}
export class PdfSqlitePreparedRunBulkStatement extends PdqSqlitePreparedStatement {
  private _params: any = undefined
  run(params: Array<any>): Promise<any> {
    const start = performance.now()
    try {
      this._params = params
      return this._client.executeStatement(this)
    } finally {
      log(`PdfSqlitePreparedRunBulkStatement: run took ${performance.now() - start}ms`)
    }

  }
  getPayload(): { id: number; verb: string; params: any } {
    return { id: this._id, params: this._params, verb: this._verb, path: this._path };
  }
}
export class PdfSqlitePreparedGetStatement extends PdqSqlitePreparedStatement {
  private _params: any = undefined
  get(params: Array<any>): Promise<any> {
    this._params = params
    return this._client.executeStatement(this)
  }
  getPayload(): { id: number; verb: string; params: any } {
    return { id: this._id, params: this._params, verb: this._verb, path: this._path };
  }
}
export class PdfSqlitePreparedAllStatement extends PdqSqlitePreparedStatement {
  private _params: any = undefined
  all(params: Array<any>): Promise<any[]> {
    this._params = params
    return this._client.executeStatement(this) as Promise<any[]>
  }
  getPayload(): { id: number; verb: string; params: any } {
    return { id: this._id, params: this._params, verb: this._verb, path: this._path };
  }
}
export class PdfSqlitePreparedBindStatement extends PdqSqlitePreparedStatement {
  private _params: any = undefined
  private _bound = false
  private _nextVerb = 'bind'
  bind(params: Array<any>): Promise<any> {
    this._bound = true
    this._params = params
    return this._client.executeStatement(this)
  }
  run(): Promise<any> {
    this._checkBinding()
    this._nextVerb = 'run'
    return this._client.executeStatement(this)
  }
  get(): Promise<any> {
    this._checkBinding()
    this._nextVerb = 'get'
    return this._client.executeStatement(this)
  }
  all(): Promise<any[]> {
    this._checkBinding()
    this._nextVerb = 'all'
    return this._client.executeStatement(this) as Promise<any[]>
  }
  getPayload(): { id: number; verb: string; params: any } {
    return { id: this._id, params: this._params, verb: this._nextVerb, path: this._path};
  }
  private _checkBinding() {
    if (!this._bound)
      throw new Error('Failed to execute... bind must be called first')
  }
}